import { useQuery } from "@tanstack/react-query";
import { fetchViewSaless } from "./api";
import Nav from "./Nav";
import { useState, useMemo } from "react";

const ViewSaless = () => {
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["viewsaless"],
    queryFn: fetchViewSaless,
  });

  // Filter data based on selected month, year and search
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.filter((item) => {
      const salesDate = new Date(item.paiddate);
      const itemMonth = (salesDate.getMonth() + 1).toString().padStart(2, "0");
      const itemYear = salesDate.getFullYear().toString();

      const monthMatch = selectedMonth === "" || itemMonth === selectedMonth;
      const yearMatch = selectedYear === "" || itemYear === selectedYear;

      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const searchMatch =
        searchTerm === "" ||
        item.name?.toLowerCase().includes(searchLower) ||
        item.area?.toLowerCase().includes(searchLower) ||
        item.mobileno?.toString().includes(searchLower) ||
        item.id?.toString().includes(searchLower);

      return monthMatch && yearMatch && searchMatch;
    });
  }, [data, selectedMonth, selectedYear, searchTerm]);

  // Get unique years from data for year dropdown
  const availableYears = useMemo(() => {
    if (!data || data.length === 0) return [];

    const years = [
      ...new Set(data.map((item) => new Date(item.paiddate).getFullYear())),
    ];
    return years.sort((a, b) => b - a); // Sort descending
  }, [data]);

  // Reset filters
  const resetFilters = () => {
    setSelectedMonth("");
    setSelectedYear("");
    setSearchTerm("");
  };

  if (isLoading) return <p className="text-center mt-5">Loading...</p>;
  if (isError)
    return (
      <p className="text-center mt-5 text-danger">Error: {error.message}</p>
    );

  return (
    <>
      <Nav />
      <div className="container-fluid mt-5">
        <h2 className="mb-4 text-primary">Latest Sales per Customer</h2>
        <h2>{filteredData.length} Results Found</h2>

        {/* Filter Section */}
        <div className="row mb-4">
          <div className="col-md-3">
            <label htmlFor="monthFilter" className="form-label">
              Filter by Month:
            </label>
            <select
              id="monthFilter"
              className="form-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value="">All Months</option>
              <option value="01">January</option>
              <option value="02">February</option>
              <option value="03">March</option>
              <option value="04">April</option>
              <option value="05">May</option>
              <option value="06">June</option>
              <option value="07">July</option>
              <option value="08">August</option>
              <option value="09">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>
          </div>

          <div className="col-md-3">
            <label htmlFor="yearFilter" className="form-label">
              Filter by Year:
            </label>
            <select
              id="yearFilter"
              className="form-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <option value="">All Years</option>
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* 🔍 Search Field */}
          <div className="col-md-3">
            <label htmlFor="search" className="form-label">
              Search (Name, Area, ID, Mobile):
            </label>
            <input
              type="search"
              id="search"
              className="form-control"
              placeholder="Enter keyword..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="col-md-3 d-flex align-items-end">
            <button className="btn btn-secondary w-100" onClick={resetFilters}>
              Reset Filters
            </button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-hover align-middle text-center">
            <thead className="table-primary">
              <tr>
                <th>Customer ID</th>
                <th>Name</th>
                <th>Area</th>
                <th>Mobile</th>
                <th>Balance</th>
                <th>Paid Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredData && filteredData.length > 0 ? (
                filteredData.map((d) => (
                  <tr key={d.sid}>
                    <td>{d.id}</td>
                    <td>{d.name}</td>
                    <td>{d.area}</td>
                    <td>{d.mobileno}</td>
                    <td>₹{d.balance}</td>
                    <td>
                      {d.paiddate
                        ? new Date(d.paiddate).toLocaleDateString("en-GB")
                        : "N/A"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center text-muted">
                    {selectedMonth || selectedYear || searchTerm
                      ? "No sales data found for the selected filter/search."
                      : "No sales data found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default ViewSaless;
